import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { applicationStore } from "../../store/ApplicationStore";
import { toJS } from "mobx";
import Webcam from "react-webcam";
import * as tf from "@tensorflow/tfjs";
import "@tensorflow/tfjs-backend-webgl";
import { detectVideo } from "../../utils/detect";
import LoadingComponent from "src/components/ui/LoadingComponent";
import { Button } from "../../components/ui/Button";
import { LeftArrowWhite } from "../../assets/images/svg/LeftArrow";
import TakeAPhotoCaptureTipsImg from "../../assets/images/TakeAPhotoCaptureTips.png";
import CaptureTipsModalImg from "../../assets/images/CaptureTipsModalImg.png";
import DoneIcon from "../../assets/images/DoneIcon.png";
import Verification from "../../assets/images/Verification.png";
import { IdentificationTypes } from "src/enums/IdentificationTypes";
import logger from "src/utils/logger";
import { getIdInstruction } from "src/helpers/idVerificationUtils";
import { resizeImageAsync } from "src/helpers/resizeImageHelper";
import { CameraPermissionStatuses } from "src/enums/CameraPermissionStatuses";
import { COUNTRY_MODEL_MAP, DEFAULT_COUNTRY_KEY } from "src/constants/countryConfig";
import { getOrientationType } from "src/helpers/getOrientationType";
import { useTranslation } from "react-i18next";
import { imageStorage } from "src/utils/imageStorage";
import { FLOW_SHADOW } from "src/theme/flowColorTokens";

const portraitConstraints = {
  width: { min: 480, ideal: 720, max: 1080 },
  height: { min: 640, ideal: 960, max: 1440 },
  facingMode: "environment",
};

const landscapeConstraints = {
  width: { min: 640, ideal: 960, max: 1440 },
  height: { min: 480, ideal: 720, max: 1080 },
  facingMode: "environment",
};

const TakeAPhoto = observer(() => {
  const PAGE_PATH_NAME = "/take-a-photo"

  const navigate = useNavigate();
  const { t } = useTranslation();
  const doneMessage = t('takePhoto.feedback.done');
  const keepSteadyMessage = t('takePhoto.feedback.keepSteady');

  const [cameraPermission, setCameraPermission] = useState("prompt");
  const [information, setInformation] = useState(
    getIdInstruction(
      applicationStore.docsType,
      !applicationStore.documentSurface.front
    )
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [model, setModel] = useState({
    net: null,
    inputShape: [1, 0, 0, 3],
  }); // init model & input shape
  const [modelLoading, setModelLoading] = useState({
    loading: true,
    progress: 0,
  }); // loading state
  const [orientation, setOrientation] = useState(getOrientationType());
  const [videoConstraints, setVideoConstraints] = useState(
    getOrientationType() === "landscape" ? landscapeConstraints : portraitConstraints
  );
  const [showButton, setShowButton] = useState(false);

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const frameDivRef = useRef(null);
  const informationRef = useRef(null);

  const modelName = "yolov8n";

  const countryName = COUNTRY_MODEL_MAP[applicationStore.country] || DEFAULT_COUNTRY_KEY;
  const isCustomModelCountry = !!COUNTRY_MODEL_MAP[applicationStore.country];
  const modelControlKey = isCustomModelCountry
    ? `${toJS(countryName)}_${toJS(applicationStore.modelCotrolName)}${!applicationStore.documentSurface.front ? "" : "_back"}`
    : applicationStore.modelCotrolName;
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleOrientationChange = () => {
    const newOrientation = getOrientationType();
    setOrientation(newOrientation);
    setVideoConstraints(
      newOrientation === "landscape"
        ? landscapeConstraints
        : portraitConstraints
    );
  };

  const storeImage = async (imageSrc) => {
    if (!applicationStore.documentSurface.front) {
      await imageStorage.saveImage("img", imageSrc);
    } else if (
      applicationStore.documentSurface.front &&
      !applicationStore.documentSurface.back
    ) {
      await imageStorage.saveImage("backImg", imageSrc);
    }
  };

  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      logger.error("No image captured from webcam.");
      return;
    }

    const sizeInKB = ((imageSrc.length * 3) / 4) / 1024;
    logger.log(`Original image size: ${sizeInKB.toFixed(2)} KB`);

    // if (sizeInKB <= 1000) {
    //   logger.log("Image is already under 1 MB, skipping resize.");
    //   await storeImage(imageSrc);
    //   return;
    // }

    try {
      const resizedImageSrc = await resizeImageAsync(imageSrc);
      const resizedSizeInKB = ((resizedImageSrc.length * 3) / 4) / 1024;
      logger.log(`Resized image size: ${resizedSizeInKB.toFixed(2)} KB`);
      await storeImage(resizedImageSrc);
    } catch (error) {
      logger.error("Error resizing image:", error);
    }
  }, [webcamRef]);

  const handleClickTakeAPhoto = async () => {
    await capture();
    navigate("/confirm-photo", {
      replace: true,
      state: {
        surface: !applicationStore.documentSurface.front ? "front" : "back",
      },
    });
  };
  const handleCameraAccessDenied = useCallback(() => {
    applicationStore.setCameraError(PAGE_PATH_NAME);
    navigate("/error-access-camera", {
      replace: true,
      state: {
        previousPage: PAGE_PATH_NAME,
      },
    });
  }, [navigate])
  useEffect(() => {
    let intervalId;
    async function checkCameraPermission() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');

        if (videoDevices.length === 0) {
          setCameraPermission("no_camera")
          applicationStore.setCameraError(PAGE_PATH_NAME)
          navigate("/error-no-camera", {
            replace: true,
            state: {
              previousPage: PAGE_PATH_NAME,
            },
          });
          return
        }

        if (navigator.permissions && navigator.permissions.query) {
          const permissionStatus = await navigator.permissions.query({ name: "camera" });
          setCameraPermission(permissionStatus.state);

          if (permissionStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
            handleCameraAccessDenied();
          }
          //not working on safari
          // permissionStatus.onchange = () => {
          //   setCameraPermission(permissionStatus.state);

          //   if (permissionStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
          //     handleCameraAccessDenied();
          //   }
          // };
          if (!intervalId) {
            intervalId = setInterval(async () => {
              const updatedStatus = await navigator.permissions.query({ name: "camera" });
              if (cameraPermission !== updatedStatus.state) {
                setCameraPermission(updatedStatus.state);
              }

              if (updatedStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
                handleCameraAccessDenied();
              }
            }, 2000);
          }
        } else {
          logger.warn("Permissions API not supported on browser.");
        }
      } catch (err) {
        logger.error("Permission query error:", err);
      }
    }
    checkCameraPermission();
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [navigate, handleCameraAccessDenied, cameraPermission]);

  useEffect(() => {
    const modelType = `_${COUNTRY_MODEL_MAP[applicationStore.country] || DEFAULT_COUNTRY_KEY}`;
    tf.setBackend("webgl");
    tf.ready().then(async () => {
      await tf.loadGraphModel(
        `${window.location.origin}/${modelName}_web_model${modelType}/model.json`,
        {
          onProgress: (fractions) => {
            setModelLoading({ loading: true, progress: fractions }); // set loading fractions
          },
        }
      ).then((yolov8) => {
        const dummyInput = tf.ones(yolov8.inputs[0].shape);
        const warmupResults = yolov8.execute(dummyInput);
        setModelLoading({ loading: false, progress: 1 });
        setModel({
          net: yolov8,
          inputShape: yolov8.inputs[0].shape,
        }); // set model & input shape

        tf.dispose([warmupResults, dummyInput]);
      }); // load model

      window.addEventListener("orientationchange", handleOrientationChange);
      return () =>
        window.removeEventListener("orientationchange", handleOrientationChange);
    });
  }, []);

  useEffect(() => {
    logger.log(orientation);
    if (webcamRef.current) {
      webcamRef.current.video.style.width =
        orientation === "portrait" ? "100%" : "auto";
      webcamRef.current.video.style.height =
        orientation === "portrait" ? "auto" : "100%";
    }
  }, [orientation]);

  useEffect(() => {
    informationRef.current = information
    if (information === doneMessage) {
      const timer = setTimeout(() => {
        navigate("/confirm-photo", {
          replace: true,
          state: {
            surface: !applicationStore.documentSurface.front ? "front" : "back",
          },
        });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [information, navigate, doneMessage]);

  return (
    <>
      {modelLoading.loading ? (
        <div className="custom-container">
          <LoadingComponent />
        </div>
      ) : (
        <>
          <div className="absolute inset-0 grid grid-rows-12 h-full text-white">
            <div className="row-span-3 px-[40px] pt-[47px] bg-Gray950 z-50">
              <div className="grid grid-cols-3">
                <Button
                  className=""
                  variant="primaryBack"
                  onClick={() => {
                    navigate("/verification", { replace: true });
                  }}
                >
                  <LeftArrowWhite />
                </Button>
                <div className="flex justify-center items-center">
                  <div className="w-[48px] h-[48px] mx-auto">
                    <figure>
                      <img alt="" className="" src={Verification} />
                    </figure>
                  </div>
                </div>
              </div>
              <div className="text-h6 font-bold text-center mt-4 text-Gray50">
                {cameraPermission === CameraPermissionStatuses.REQUESTING_CAMERA_PERMISSION || cameraPermission === CameraPermissionStatuses.ACCESS_DENIED ? (
                  t('cameraPermission.browser')
                ) : (
                  getIdInstruction(
                    applicationStore.docsType,
                    !applicationStore.documentSurface.front
                  )
                )
                }
              </div>
            </div>
            <div className="row-span-9 grid grid-rows-24 bg-Gray950">
              <div className="relative row-span-11">
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  autoPlay
                  forceScreenshotSourceSize
                  screenshotFormat="image/jpeg"
                  className="absolute h-full w-full rounded-t-[16px]"
                  videoConstraints={videoConstraints}
                  onUserMedia={() => {
                    logger.log("onUserMedia")
                    setCameraPermission(CameraPermissionStatuses.ACCESS_GRANTED);
                  }}
                  onUserMediaError={(e) => {
                    logger.log(e)
                  }}
                  onPlay={() =>
                    detectVideo(
                      webcamRef.current.video,
                      model,
                      canvasRef.current,
                      {
                        frameDivRef,
                        capture,
                        setInformation,
                        information,
                        informationRef,
                        process: "takePhoto",
                        countryAndModelControlName: modelControlKey,
                        setShowButton,
                        country: applicationStore.country,
                      }
                    )
                  }
                />
                <canvas
                  className="absolute h-full w-full top-0 left-0"
                  width={model.inputShape[1]}
                  height={model.inputShape[2]}
                  ref={canvasRef}
                />
              </div>
              <div className="absolute top-0 left-0 w-full h-full z-10">
                <div className="relative h-full w-full overflow-hidden">
                  <div
                    className={`absolute left-1/2 top-42% flex flex-row justify-center items-center bg-transparent rounded-[16px] ${applicationStore.docsType === IdentificationTypes.PASSPORT
                      ? "w-[320px] h-[220px]"
                      : "h-[198px] w-[313px]"
                      } opacity-80 ${information === doneMessage ||
                        information === keepSteadyMessage
                        ? "border-[2px] border-SuccessGreen300"
                        : ""
                      }`}
                    style={{
                      boxShadow: FLOW_SHADOW.vignettePhone,
                      transform: "translate(-50%, -50%)",
                    }}
                    ref={frameDivRef}
                  >
                    {information === doneMessage ? (
                      <figure>
                        <img alt="" className="" src={DoneIcon} />
                      </figure>
                    ) : null}
                  </div>
                </div>
              </div>
              {cameraPermission === CameraPermissionStatuses.ACCESS_GRANTED &&
                <div className="row-span-13 z-20">
                  <div className="pt-10 flex justify-center flex-col items-center px-4">
                    <div className="text-h5 font-bold text-center text-Gray50 mb-6 order-1">
                      <div className="w-80">
                        <span className="block overflow-hidden text-ellipsis line-clamp-2 h-[5em]">
                          {information}
                        </span>
                      </div>
                    </div>
                    <div
                      className="bg-Gray900 p-3 rounded-[16px] flex cursor-pointer order-2"
                      onClick={toggleModal}
                    >
                      <div className="w-[16px] h-[16px] me-2">
                        <figure>
                          <img
                            alt=""
                            className="border-Gray50 rounded-[5px]"
                            src={TakeAPhotoCaptureTipsImg}
                          />
                        </figure>
                      </div>
                      <div className="underline text-xs">{t('takePhoto.captureTips')}</div>
                    </div>
                    {showButton && (
                      <div className="flex justify-center cursor-pointer mt-8 w-full order-3">
                        <Button
                          className="mt-4 w-[300px]"
                          variant="primaryWithoutIcon"
                          onClick={handleClickTakeAPhoto}
                        >
                          {t('takePhoto.takePhoto')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              }
            </div>
          </div>
          {isModalOpen && (
            <div
              className="z-50 absolute inset-0 flex justify-center flex-col items-center bg-gray-950 bg-opacity-80 overflow-y-auto h-full w-full"
              id="my-modal"
            >
              <div className="relative border w-[313px] h-[654px] shadow-lg rounded-[16px] bg-Gray50">
                <div className="mt-3 text-center">
                  <div className="mx-auto flex items-center justify-center">
                    <div className="text-h5 font-bold text-center mt-6 text-flow-heading">{t('takePhoto.captureTips')}</div>
                  </div>
                  <div className="mt-2 px-7 py-3 text-center">
                    <p className="text-gray900">{t('takePhoto.captureTipsIntro')}</p>
                    <div className="w-[160px] h-[160px] mx-auto mt-4">
                      <figure>
                        <img alt="" className="" src={CaptureTipsModalImg} />
                      </figure>
                    </div>
                    <ul className="list-disc list-inside text-Gray900 mt-3">
                      <li>{t('takePhoto.tip1')}</li>
                      <li>{t('takePhoto.tip2')}</li>
                      <li>{t('takePhoto.tip3')}</li>
                    </ul>
                  </div>
                </div>
                <div className="items-center px-4 py-3 bottom-0">
                  <Button
                    id="ok-btn"
                    className="mt-1"
                    variant="primaryMediumWithoutIcon"
                    onClick={toggleModal}
                  >
                    {t('actions.continue')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
});

export default TakeAPhoto;
